import React, { useState, useEffect } from "react";
import { trackConversion } from "@/lib/gads";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Clock, CheckCircle2, ArrowRight, AlertCircle } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [f, setF] = useState({ name: "", email: "", phone: "", country: "Nederland", password: "", role: "buyer" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // {email, name} — only set on HTTP 200 pending=true response
  const [err, setErr] = useState({});           // field-level errors
  const [serverError, setServerError] = useState("");  // top-of-form server error

  // Soft client-side validators — purely to help the user, backend is authoritative.
  const validate = () => {
    const e = {};
    if (!f.name || f.name.trim().length < 2) e.name = t("register.err_name");
    if (!/^\S+@\S+\.\S+$/.test(f.email || "")) e.email = t("register.err_email");
    if (f.phone) {
      const digits = (f.phone.match(/\d/g) || []).length;
      if (digits < 6 || digits > 20 || !/^[+\d\s\-().\/]{6,30}$/.test(f.phone.trim())) {
        e.phone = t("register.err_phone");
      }
    }
    if (!f.password || f.password.length < 6) e.password = t("register.err_password");
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) {
      // give focus to first error field
      const first = Object.keys(err)[0];
      const el = first && document.querySelector(`[data-testid="register-${first}"]`);
      if (el?.focus) el.focus();
      return;
    }
    setBusy(true);
    try {
      const result = await register({
        name: f.name.trim(),
        email: f.email.trim(),
        phone: f.phone.trim() || undefined,
        country: f.country?.trim() || undefined,  // vrije tekst — backend normaliseert of mapt naar OTHER
        password: f.password,
        role: f.role,
      });
      // ONLY show pending screen on an actual pending=true response from backend
      if (result && result.pending === true && result.id) {
        setPending({ email: result.email, name: result.name });
      } else if (result && result.id) {
        // Shouldn't happen in current workflow, but fail-safe:
        toast.success(t("register.created"));
      } else {
        // Unknown response shape — treat as error
        setServerError(t("register.err_server_unexpected"));
      }
    } catch (err) {
      const msg = formatApiError(err);
      setServerError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return <PendingTracker>{(

      <div className="min-h-[70vh] grid place-items-center px-4 py-16">
        <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="register-pending">
          <div className="w-14 h-14 rounded-full bg-[color:var(--wv-green)]/10 grid place-items-center mx-auto">
            <Clock className="w-7 h-7 text-[color:var(--wv-green-dark)]" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-extrabold text-[color:var(--wv-obsidian)]">{t("register.pending_title")}</h1>
          <p className="mt-3 text-slate-600">{t("register.pending_body_prefix")} <strong>{pending.email}</strong>. {t("register.pending_body_suffix")}</p>
          <ul className="mt-6 text-left space-y-2">
            {[t("register.pending_step1"), t("register.pending_step2"), t("register.pending_step3")].map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-[color:var(--wv-green)] mt-0.5 flex-shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <Link to="/" className="mt-8 inline-flex wv-btn-primary" data-testid="register-pending-home">
            {t("back_home")} <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </div>
    )}</PendingTracker>;
  }

  return (
    <div className="min-h-[70vh] grid place-items-center px-4 py-16">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8" noValidate>
        <h1 className="font-display text-3xl font-extrabold text-[color:var(--wv-obsidian)]">{t("register.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("register.subtitle_approval")}</p>

        {serverError && (
          <div className="mt-5 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2" data-testid="register-server-error">
            <AlertCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">{serverError}</p>
          </div>
        )}

        <div className="mt-6 space-y-3">
          <div>
            <label className="text-sm font-semibold">{t("register.name")}</label>
            <input className="wv-input mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name" />
            {err.name && <p className="text-xs text-red-600 mt-1" data-testid="register-err-name">{err.name}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">{t("register.email")}</label>
            <input className="wv-input mt-1" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email" />
            {err.email && <p className="text-xs text-red-600 mt-1" data-testid="register-err-email">{err.email}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">{t("register.phone")}</label>
            <input className="wv-input mt-1" type="tel" placeholder="+31 6 12 34 56 78 of 06-12345678" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="register-phone" />
            {err.phone && <p className="text-xs text-red-600 mt-1" data-testid="register-err-phone">{err.phone}</p>}
            <p className="text-xs text-slate-400 mt-1">{t("register.phone_hint")}</p>
          </div>
          <div>
            <label className="text-sm font-semibold">{t("register.country")}</label>
            <input className="wv-input mt-1" placeholder={t("register.country_hint")} value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="register-country" />
            <p className="text-xs text-slate-400 mt-1">{t("register.country_hint")}</p>
          </div>
          <div>
            <label className="text-sm font-semibold">{t("register.password")}</label>
            <input className="wv-input mt-1" type="password" minLength="6" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password" />
            {err.password && <p className="text-xs text-red-600 mt-1" data-testid="register-err-password">{err.password}</p>}
          </div>
          <div>
            <label className="text-sm font-semibold">{t("register.role_label")}</label>
            <div className="mt-2 flex gap-2">
              {[["buyer", t("register.role_buyer")], ["seller", t("register.role_seller")]].map(([v, label]) => (
                <button type="button" key={v} onClick={() => setF({ ...f, role: v })} className={`wv-chip ${f.role === v ? "active" : ""}`} data-testid={`register-role-${v}`}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        <button className="mt-6 w-full wv-btn-primary" disabled={busy} onClick={trackConversion} data-testid="register-submit">{busy ? t("register.submitting") : t("register.submit")}</button>
        <p className="mt-4 text-sm text-slate-500 text-center">{t("register.have_account")} <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-[color:var(--wv-navy)] font-semibold hover:underline">{t("register.login_link")}</Link></p>
      </form>
    </div>
  );

function PendingTracker({ children }) {
  useEffect(() => { trackConversion(); }, []);
  return children;
}

}
